import {useState} from 'react';
import {useId} from '@sail/react-aria';
import {
  chevronDown,
  chevronRight,
  chevronUp,
  chevronDownCircle,
  chevronRightCircle,
  chevronUpCircle,
} from '@sail/icons/react/Icon';
import type {IconAsset} from '@sail/icons/types';

const iconMap = {
  chevron: {
    'down-up': {
      closed: chevronDown,
      open: chevronUp,
    },
    'right-down': {
      closed: chevronRight,
      open: chevronDown,
    },
  },
  'chevron-circle': {
    'down-up': {
      closed: chevronDownCircle,
      open: chevronUpCircle,
    },
    'right-down': {
      closed: chevronRightCircle,
      open: chevronDownCircle,
    },
  },
};

export interface UseCollapsibleProps {
  /**
   * Whether or not to show the CollapsibleArea by default
   * @deprecated use `open` prop instead
   */
  defaultOpen?: boolean;
  /**
   * Allow for controlled usage of open state
   */
  open?: boolean;
  /**
   * Orientation for icon
   */
  iconOrientation?: 'down-up' | 'right-down';
  /**
   * Type of icon to use, either a check or a check inside of a circle
   */
  iconType?: 'chevron' | 'chevron-circle';
}

export interface UseCollapsibleReturn {
  /** @deprecated use external state to set the open state, pass that state to `open` in `useCollapsible` */
  open: boolean;
  /** @deprecated use external state to set the open state, pass that state to `open` in `useCollapsible` */
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerProps: {
    id: string;
    'aria-expanded': boolean;
    'aria-controls': string;
  };
  collapsibleProps: {
    id: string;
    open: boolean;
    'aria-labelledby': string;
  };
  icon: IconAsset;
}

export const useCollapsible = (
  config: UseCollapsibleProps = {},
): UseCollapsibleReturn => {
  const panelId = useId();
  const buttonId = useId();

  const [openUncontrolled, setOpen] = useState(config.defaultOpen || false);
  const isOpen = config.open ?? openUncontrolled;
  const iconType = config.iconType ?? 'chevron';
  const iconOrientation = config.iconOrientation ?? 'down-up';

  const icon = iconMap[iconType][iconOrientation][isOpen ? 'open' : 'closed'];

  return {
    open: isOpen, // Deprecated
    setOpen, // Deprecated
    triggerProps: {
      id: buttonId,
      'aria-expanded': isOpen,
      'aria-controls': panelId,
    },
    collapsibleProps: {
      'aria-labelledby': buttonId,
      id: panelId,
      open: isOpen,
    },
    icon,
  };
};
